import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import { FormikHelpers } from 'formik';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Redirect, useHistory, useLocation } from 'react-router';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { MaritalStatuses } from '../../../../global/constants/lists';
import { apiErrorClear } from '../../../../global/ducks/base-api';
import { ProcessName, RegisterCompletedProcess, RegisterStartProcess } from '../../../../global/helpers/analytics-helper';
import { getCurrentTimeNY, getNumberOfDays } from '../../../../global/helpers/date-helpers';
import { formatDate } from '../../../../global/helpers/form-data-helper';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { useParentRoute } from '../../../../global/hooks/useParentRoute';
import { ApiStatuses, BaseApiState } from '../../../../global/models/api-model';
import { DemographicAppKeys, DemographicsContext } from '../../../../global/models/demographics-model';
import { Person } from '../../../../global/models/person-model';
import { TcsmEpiApiState, TcsmStates } from '../../../../root/tcsm-model';
import { setAEStep } from '../../../annual-enrollment/annual-enrollment-slice';
import { AESteps, AnnualEnrollmentContext, AnnualEnrollmentKey } from '../../../annual-enrollment/models/annual-enrollment-models';
import { clearBeneficiaries, clearBeneficiariesRelationships } from '../../../arc/features/beneficiary/beneficiary-slices';
import { clearMemberNewHireEnrollmentCoverage, clearNewHireEnrollmentCoverage } from '../../../arc/features/new-hire-enrollment/slice/new-hire-enrollment-slices';
import { OSCRRService } from '../../../arc/features/person-search/oscrr-helpers';
import { PersonSearchContext, PersonSearchKeys, PersonSearchState, SearchPerson } from '../../../arc/features/person-search/person-search-model';
import { AnnualEnrollmentProps } from '../../../arc/models/arc-epi-props';
import { MyCpgMidYearPersonalInfoAlertTypes } from '../../../notification/notification-models';
import { AccountInfoRoutes } from '../../constants/routes';
import { useDependents } from '../../hooks/useDependents';
import { useSpouseInformation } from '../../hooks/useSpouseInfo';
import { FormTypes } from '../../models/form-models';
import { DependentsState } from '../dependents/dependents-model';
import { createEnrollmentWindow } from '../mycpg-midyear/helpers/mycpg-midyear-coverage-form-helpers';
import { PointOfContactFormModel, QualifyingEventType, PointOfContactDepentType } from '../mycpg-midyear/models/mycpg-mid-year-models';
import { DocumentUploadState } from '../personal-info/document-upload-models';
import { setDocumentUploadStatus, stageDocuments } from '../personal-info/document-upload-slice';
import { setPatchPersonStatus } from '../personal-info/personal-info-slice';
import PointOfContact from '../point-of-contact/point-of-contact';
import { RelationshipsState } from '../relationships/relationships-model';
import { determineCurrentSpouse, getCurrentDomesticPartner } from '../relationships/relationships-selectors';
import { setDivorcedStatus, setLegalSeparationStatus, setPatchSpouseStatus, setPostSpouseStatus, setSingleStatus, setSpouseUnloaded, setWidowedStatus } from '../relationships/relationships-slice';
import { MaritalStatusFormValues } from './marital-form-models';
import { handleDivorceOrWidowed, handleMarriedChange, handleSubmitPost, handleSubmitPut } from './marital-requests';
import { MaritalStatusForm } from './marital-status-form';

interface Props {
	basePath?: string;
	skipDocUpload?: boolean;
}

interface MaritalStatusState {
	values?: MaritalStatusFormValues;
	savedValues?: MaritalStatusFormValues;
	formHelpers?: FormikHelpers<MaritalStatusFormValues>;
	selectedPerson?: SearchPerson;
	documentUploadState?: DocumentUploadState;
}

export function MaritalStatusApp(props: Props) {
	const apiState = useSelector<TcsmStates, BaseApiState>(state => state.apiState);
	const personSearchKey = PersonSearchKeys.MaritalStatus;
	const personSearchState = useSelector<TcsmStates, PersonSearchState>(state => state.personSearchState[personSearchKey]);
	const location = useLocation();

	const app = useContext(DemographicsContext);
	const enrollmentContext = useContext(AnnualEnrollmentContext);
	const isNewHireEnrollment = enrollmentContext === AnnualEnrollmentKey.newHireEnrollment;
	const isAnnualEnrollment = enrollmentContext === AnnualEnrollmentKey.annualEnrollment || enrollmentContext === AnnualEnrollmentKey.participantEnrollment;
	const annualEnrollmentSettings = useSelector<TcsmStates, AnnualEnrollmentProps>(state => state.epiProps?.annualEnrollmentSettingsState?.epiContent);
	const personGuid = useSelector<TcsmStates, string>(state => state.personalInfo[app]?.currentPerson?.person_guid);
	const relationshipsState = useSelector<TcsmStates, RelationshipsState>(state => state.relationships[app]);
	const dependentsState = useSelector<TcsmStates, DependentsState>(state => state.dependents[app]);
	const person = useSelector<TcsmStates, Person>(state => state.personalInfo[app]?.currentPerson);
	const epiProps = useSelector<TcsmStates, TcsmEpiApiState>(state => state.epiProps);
	const docUploadState = useSelector<TcsmStates, DocumentUploadState>(state => state.documentUpload[app]);
	const spousesLoaded = useSpouseInformation(personGuid);
	const dependentsLoaded = useDependents(personGuid);
	const history = useHistory();
	const [showConfirmation, setShowConfirmation] = useState(false);
	const [showPointOfContact, setShowPointOfContact] = useState(false);
	const [currentMaritalStatusState, setCurrentMaritalStatusState] = useState<MaritalStatusState>(null);

	const parentRoute = useParentRoute();
	const dispatch = useDispatch();
	const pageRef = useRef<HTMLDivElement>(null);
	const relationshipPage = `${props.basePath}${AccountInfoRoutes.RelationshipsSegment}`;
	const myCpgMidYearPath = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.Midyear}`);
	const relationshipsNoPropsPage = useBasePath(`${AccountInfoRoutes.MyCpgRoot}${AccountInfoRoutes.RelationshipsSegment}`);
	const midYearEnrollmentDivorce = `${myCpgMidYearPath}${AccountInfoRoutes.PersonalInfoSegment}?alerts=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE},${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE_DIVORCE}`;
	const midYearEnrollmentMarriage = `${myCpgMidYearPath}${AccountInfoRoutes.PersonalInfoSegment}?alerts=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE},${MyCpgMidYearPersonalInfoAlertTypes. LIFE_EVENT_UPDATE_MARRIAGE}`;
	const currentPathisMidYearEnrollment = location.pathname.includes(myCpgMidYearPath);
	const midYearEnrollmentLegalSeparation = `${myCpgMidYearPath}${AccountInfoRoutes.PersonalInfoSegment}?alerts=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE},${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE_LEGAL_SEPARATION}`;
	const currentDomesticPartner = getCurrentDomesticPartner(dependentsState?.dependents?.domesticPartners);
	function GetMaritalDescription(): string {
		if (isAnnualEnrollment && (app === DemographicAppKeys.myCPG)) {
			return epiProps?.annualEnrollmentSettingsState?.epiContent?.maritalDescription || epiProps?.relationshipsSettingState?.epiContent?.maritalDescription;
		}

		if (isAnnualEnrollment && (app === DemographicAppKeys.arc)) {
			return epiProps?.annualEnrollmentSettingsState?.epiContent?.maritalDescriptionMAP || epiProps?.relationshipsSettingState?.epiContent?.maritalDescription;
		}

		return epiProps?.relationshipsSettingState?.epiContent?.maritalDescription;
	}

	const maritalDescription = GetMaritalDescription();

	RegisterStartProcess(ProcessName.MaritalStatus);

	useEffect(() => {
		dispatch(apiErrorClear());
		dispatch(setDocumentUploadStatus(false, app));
		dispatch(setWidowedStatus(ApiStatuses.None, app));
		dispatch(setDivorcedStatus(ApiStatuses.None, app));
		dispatch(setPostSpouseStatus(ApiStatuses.None, app));
		dispatch(setPatchSpouseStatus(ApiStatuses.None, app));
		dispatch(setPatchPersonStatus(ApiStatuses.None, app));
		dispatch(setLegalSeparationStatus(ApiStatuses.None, app));

	}, []);

	useEffect(() => {
		if (isAnnualEnrollment) {
			dispatch(setAEStep(AESteps.Relationships, app));
		}
	}, []);

	if (!spousesLoaded || !dependentsLoaded) {
		return <LoadingCircle />;
	}

	if (currentDomesticPartner) {
		return <Redirect to={`${app === DemographicAppKeys.myCPG ? relationshipsNoPropsPage : relationshipPage}?warning=${FormTypes.HAVE_DOMESTIC_PARTNER_WARNING}`} />;
	}


	const currentSpouse = determineCurrentSpouse(person?.marital_status, relationshipsState?.spouses);
	const currentSpousePerson = currentSpouse?.person;



	const dependentType: PointOfContactDepentType = PointOfContactDepentType.Spouse;

	async function OnSubmitHandler(
		values: MaritalStatusFormValues,
		savedValues: MaritalStatusFormValues,
		formHelpers: FormikHelpers<MaritalStatusFormValues>,
		selectedPerson: SearchPerson,
		pointOfContactFormValues?: PointOfContactFormModel,
		submitDocumentUploadState?: DocumentUploadState) {

		// Check if new results were found after click.
		if (await OSCRRService.SearchHasNewResultsAsync(apiState, personSearchState)) return;

		// Set current state to keep it for the next view in the flow.
		if (submitDocumentUploadState) {
			setCurrentMaritalStatusState(
				{
					...currentMaritalStatusState,
					values,
					savedValues,
					formHelpers,
					selectedPerson,
				});
		} else {
			setCurrentMaritalStatusState(
				{
					...currentMaritalStatusState,
					values,
					savedValues,
					formHelpers,
					selectedPerson,
					documentUploadState: docUploadState,
				});
		}

		// Adjust address mask.
		const toUpdate: MaritalStatusFormValues = {
			...values,
			mailing_address_1: values.mailing_address_1 === currentSpousePerson?.masked_mailing_address_1 ? (currentSpousePerson?.masked_mailing_address_1 || '') : values.mailing_address_1,
			mailing_address_2: values.mailing_address_2 === currentSpousePerson?.masked_mailing_address_2 ? (currentSpousePerson?.masked_mailing_address_2 || '') : values.mailing_address_2,
			mailing_address_3: values.mailing_address_3 === currentSpousePerson?.masked_mailing_address_3 ? (currentSpousePerson?.masked_mailing_address_3 || '') : values.mailing_address_3,
		};

		function FormSuccessAction() {
			setShowPointOfContact(false);
			formHelpers.resetForm();

			dispatch(clearBeneficiaries());
			dispatch(clearBeneficiariesRelationships());

			dispatch(clearMemberNewHireEnrollmentCoverage());
			dispatch(clearNewHireEnrollmentCoverage());

			RegisterCompletedProcess(ProcessName.MaritalStatus);

			const midyearInProgressWarningAdditionalQueryString = enrollmentContext !== AnnualEnrollmentKey.newHireEnrollment ? `,${FormTypes.in_progress_mid_year_warning_marital_event}` : '';


			if (isNewHireEnrollment && app === DemographicAppKeys.myCPG) {
				history.push(`${relationshipPage}?success=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE_MARRIAGE}${midyearInProgressWarningAdditionalQueryString}`);
				return;
			}

			if (app === DemographicAppKeys.myCPG && values?.marital_status === MaritalStatuses.Married && values?.marital_status_effective_date && !currentPathisMidYearEnrollment) {
				const currentDate = getCurrentTimeNY();
				const numberOfDays: number = getNumberOfDays(values?.marital_status_effective_date, currentDate);

				if (numberOfDays <= 30) {
					createEnrollmentWindow(
						personGuid,
						{
							enrollment_window: {
								qualifying_event: QualifyingEventType.marriage,
								qualifying_event_date: formatDate(values?.marital_status_effective_date),
							},
						},
						() => history.push(midYearEnrollmentMarriage),
						() => history.push(`${relationshipsNoPropsPage}?success=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE_MARRIAGE}${midyearInProgressWarningAdditionalQueryString}`),
						dispatch,
					);
					return;
				}
			}

			if (app === DemographicAppKeys.myCPG && (values?.marital_status === MaritalStatuses.Divorced || values?.marital_status === MaritalStatuses.LegallySeparated) && values?.marriage_end_date) {
				const currentDate = getCurrentTimeNY();
				const numberOfDays: number = getNumberOfDays(values?.marriage_end_date, currentDate);
				const qualifying_event_type = values?.marital_status === MaritalStatuses.Divorced ? QualifyingEventType?.divorce : QualifyingEventType?.legal_separation;

				if (numberOfDays < 30) {
					createEnrollmentWindow(
						personGuid,
						{
							enrollment_window: {
								qualifying_event: qualifying_event_type,
								qualifying_event_date: formatDate(values?.marriage_end_date),
							},
						},
						() => history.push(values?.marital_status === MaritalStatuses.Divorced ? midYearEnrollmentDivorce : midYearEnrollmentLegalSeparation),
						() => history.push(`${relationshipsNoPropsPage}?success=${MyCpgMidYearPersonalInfoAlertTypes.LIFE_EVENT_UPDATE_MARRIAGE}${midyearInProgressWarningAdditionalQueryString}`),
						dispatch,
					);

				} else {
					history.push(`${relationshipsNoPropsPage}?success=${FormTypes.NEW_HIRE_SPOUSE_UPDATE_SUCCESS_WITH_WARNING}`);
				}
			}

			if (values?.marital_status === MaritalStatuses.Widowed && !currentSpouse?.person?.preliminary_date_of_death) {
				history.push(`${relationshipPage}?success=${app === DemographicAppKeys?.arc ? FormTypes.MAP_MARITAL_STATUS_WIDOW_SUCCESS : FormTypes.MARITAL_STATUS_WIDOW_SUCCESS}`);
				return;
			}

			if (values?.marital_status === MaritalStatuses.Widowed && currentSpouse?.person?.preliminary_date_of_death) {
				dispatch(setSpouseUnloaded(app));
				return;
			}

			if (docUploadState?.stagedDocuments?.length > 0) {
				history.push(`${relationshipPage}?success=${app === DemographicAppKeys?.arc ? FormTypes.MAP_MARITAL_STATUS_DOC_UPLOAD_SUCCESS : FormTypes.MARITAL_STATUS_DOC_UPLOAD_SUCCESS}`);
				return;
			}

			if (app === DemographicAppKeys?.arc) {

				if (values?.marital_status === MaritalStatuses.LegallySeparated) {
					history.push(`${relationshipPage}?success=${FormTypes.MAP_LEGAL_SEPERATION}`);
					return;
				}

				if (values?.marital_status === MaritalStatuses.Married || values?.marital_status === MaritalStatuses.Divorced) {
					history.push(`${relationshipPage}?success=${FormTypes.MAP_MARITAL_STATUS}`);
					return;
				}
			}

			if (isAnnualEnrollment) {
				history.push(`${relationshipPage}?success=${FormTypes.AE_SPOUSE_UPDATE_SUCCESS}`);
			}
		}


		if (toUpdate.marital_status === MaritalStatuses.Married) {
			if (person?.marital_status !== MaritalStatuses.Married && app === DemographicAppKeys.arc && !showConfirmation) {
				setShowConfirmation(true);
				window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
				dispatch(apiErrorClear());
				formHelpers.setTouched({});
				formHelpers.setSubmitting(false);

				return;
			}

			if (selectedPerson) {
				handleSubmitPut(docUploadState, app, dispatch, personGuid, toUpdate.marital_status_effective_date, selectedPerson.guid, FormSuccessAction);
			} else if (!currentSpouse || person.marital_status !== MaritalStatuses.Married) {
				// add new spouse
				handleSubmitPost(docUploadState, app, dispatch, personGuid, savedValues, toUpdate, FormSuccessAction);
			} else {
				// update current spouse
				handleMarriedChange(app, dispatch, personGuid, savedValues, toUpdate, currentSpouse, docUploadState, personGuid, FormSuccessAction, props?.skipDocUpload);
			}
		} else if ((toUpdate.marital_status === MaritalStatuses.Widowed && !currentSpouse?.person?.preliminary_date_of_death) && (!showPointOfContact || !pointOfContactFormValues)) {
			setShowPointOfContact(true);
		} else if (toUpdate.marital_status === MaritalStatuses.Widowed || toUpdate.marital_status === MaritalStatuses.Divorced || toUpdate.marital_status === MaritalStatuses.LegallySeparated) {
			// divorce or widow current spouse
			handleDivorceOrWidowed(submitDocumentUploadState || docUploadState, app, dispatch, personGuid, person?.marital_status, currentSpouse, savedValues, toUpdate, toUpdate.marital_status === MaritalStatuses.Widowed, pointOfContactFormValues, FormSuccessAction);
		} else if (toUpdate.marital_status === MaritalStatuses.Single) {
			dispatch(setSingleStatus(ApiStatuses.Success, app));
		}

		setShowConfirmation(false);
	}

	function OnPointOfContactSubmit(pointOfContactFormValues: PointOfContactFormModel) {
		if (currentMaritalStatusState?.formHelpers &&
			currentMaritalStatusState?.savedValues &&
			currentMaritalStatusState?.values) {

			OnSubmitHandler(
				currentMaritalStatusState?.values,
				currentMaritalStatusState?.savedValues,
				currentMaritalStatusState?.formHelpers,
				currentMaritalStatusState?.selectedPerson,
				pointOfContactFormValues,
				currentMaritalStatusState?.documentUploadState);
		}
	}

	function OnPointOfContactCancel() {
		setShowPointOfContact(false);

		const loadedFiles = currentMaritalStatusState?.documentUploadState?.stagedDocuments;
		// If there are files loaded, stage them again
		// The document state is reset when the contact form is displayed.
		if (loadedFiles && loadedFiles.length > 0) {
			dispatch(stageDocuments(loadedFiles, app));
		}
	}

	return (
		<div ref={pageRef}>
			{
				isAnnualEnrollment && app === DemographicAppKeys.arc &&
				<ContentSpacer>
					<div dangerouslySetInnerHTML={{ __html: annualEnrollmentSettings?.adminAccessMessage }} />
				</ContentSpacer>
			}

			{ /* Hide the form but keep it rendering to keep the state */}
			<div className={showPointOfContact ? 'hide' : ''}>
				<PersonSearchContext.Provider value={PersonSearchKeys.MaritalStatus}>
					{(OSCRRService.IsOSCRRWorking(apiState) ? <LoadingCircle /> : null)}

					<MaritalStatusForm
						formHeader={epiProps?.relationshipsSettingState?.epiContent?.maritalStatusTitle}
						formDescription={maritalDescription}
						currentPerson={person}
						spouse={currentSpouse}
						relationshipsProps={epiProps.relationshipsSettingState.epiContent}
						labelSetting={epiProps.labelSettingState.epiContent}
						onSubmitHandler={OnSubmitHandler}
						previousRoute={parentRoute}
						showConfirmation={showConfirmation}
						setShowConfirmation={setShowConfirmation}
						skipDocUpload={props?.skipDocUpload}
					/>
				</PersonSearchContext.Provider>
			</div>

			{showPointOfContact && <PointOfContact isMyCpg={app === DemographicAppKeys.myCPG} onSubmitHandler={OnPointOfContactSubmit} onCancelHandler={OnPointOfContactCancel} dependentType={dependentType} />}
		</div>
	);
}

MaritalStatusApp.defaultProps = {
	basePath: '',
	skipDocUpload: false,
};